/* PDP image zoom lightbox.

   The overlay's slider is a real Swiper instance — the same library and
   settings family as the main PDP gallery (#productSwiper in global.js) —
   so swiping between images feels identical to swiping the gallery
   itself. Swiper's zoom module owns pinch, double-tap and pan; the
   zoomChange handler below owns the swipe lock while zoomed.

   The instance is rebuilt on every open: slides come from whatever the
   clicked carousel is currently showing (currentSrc), which tracks
   responsive srcset picks and variant image switching for free. */

class ProductImageZoom {
	constructor() {
		this.overlay = null
		this.swiper = null
		this.images = []
		this.openedBy = null
		this.pendingScale = null
		this.zoomFrame = null

		this.createOverlay()
		this.attachClickHandler()
		this.attachKeyboardHandlers()
	}

	createOverlay() {
		this.overlay = document.createElement('div')
		this.overlay.className = 'image-zoom-overlay'
		/* Single-finger touchmove inside the overlay must reach Swiper
		   while the body scroll lock's touch guard is active (global.js). */
		this.overlay.setAttribute('data-co-scroll-allowed', '')
		/* Focusable so trapFocus can land on the overlay itself — see
		   open() for why the close button must not take initial focus. */
		this.overlay.setAttribute('tabindex', '-1')
		this.overlay.innerHTML = `
      <button class="zoom-close" aria-label="Close zoom">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
      <button class="zoom-nav zoom-prev" aria-label="Previous image">
        <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M13.7071 6.2929C14.0976 6.68342 14.0976 7.31658 13.7071 7.7071L7.41422 14H25C25.5522 14 26 14.4477 26 15C26 15.5523 25.5522 16 25 16H7.41422L13.7071 22.2928C14.0976 22.6834 14.0976 23.3166 13.7071 23.7072C13.3166 24.0976 12.6834 24.0976 12.2929 23.7072L4.2929 15.7071C3.90236 15.3166 3.90236 14.6834 4.2929 14.2929L12.2929 6.2929C12.6834 5.90236 13.3166 5.90236 13.7071 6.2929Z" fill="white"></path>
        </svg>
      </button>
      <button class="zoom-nav zoom-next" aria-label="Next image">
        <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M16.2929 6.2929C16.6834 5.90236 17.3166 5.90236 17.7071 6.2929L25.7072 14.2929C26.0976 14.6834 26.0976 15.3166 25.7072 15.7071L17.7071 23.7072C17.3166 24.0976 16.6834 24.0976 16.2929 23.7072C15.9024 23.3166 15.9024 22.6834 16.2929 22.2928L22.5858 16H5C4.44772 16 4 15.5523 4 15C4 14.4477 4.44772 14 5 14H22.5858L16.2929 7.7071C15.9024 7.31658 15.9024 6.68342 16.2929 6.2929Z" fill="white"></path>
        </svg>
      </button>
      <div class="swiper zoom-swiper">
        <div class="swiper-wrapper"></div>
      </div>
    `

		document.body.appendChild(this.overlay)

		this.overlay
			.querySelector('.zoom-close')
			.addEventListener('click', () => this.close())
		this.overlay
			.querySelector('.zoom-prev')
			.addEventListener('click', () => this.navigate(-1))
		this.overlay
			.querySelector('.zoom-next')
			.addEventListener('click', () => this.navigate(1))
		/* The slider covers the whole overlay, so "click the backdrop to
		   close" means the empty space around the image — the
		   swiper-zoom-container, but never the image itself (that's
		   Swiper's double-tap zoom target). Swiper suppresses clicks that
		   end a drag, so swipes can't trigger this. */
		this.overlay.addEventListener('click', (e) => {
			if (
				e.target === this.overlay ||
				e.target.classList.contains('swiper-zoom-container')
			) {
				this.close()
			}
		})
		/* Trackpad/mouse-wheel zoom. Pinch arrives as a ctrlKey wheel
		   event (its deltaY tracks the pinch, so scale follows the
		   gesture); a plain vertical scroll steps the zoom like the old
		   overlay did. Horizontal two-finger swipes are left for Swiper's
		   mousewheel module, which turns them into slide changes.
		   Application is coalesced to one zoom.in() per frame — it forces
		   a layout pass, and trackpads emit far more events than frames. */
		this.overlay.addEventListener(
			'wheel',
			(e) => {
				if (!this.swiper) return
				if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && !e.ctrlKey) return
				e.preventDefault()
				const { minRatio, maxRatio } = this.swiper.params.zoom
				const step = e.ctrlKey
					? -e.deltaY * 0.01
					: e.deltaY > 0
					? -0.1
					: 0.1
				const current = this.pendingScale ?? this.swiper.zoom.scale
				const target = Math.max(
					minRatio,
					Math.min(maxRatio, current + step)
				)
				if (target === current) return
				this.pendingScale = target
				if (this.zoomFrame) return
				this.zoomFrame = requestAnimationFrame(() => {
					this.zoomFrame = null
					const scale = this.pendingScale
					this.pendingScale = null
					if (!this.swiper || scale === null) return
					if (scale <= 1) this.swiper.zoom.out()
					else this.swiper.zoom.in(scale)
				})
			},
			{ passive: false }
		)
	}

	attachClickHandler() {
		/* One delegated listener covers every gallery image, including
		   any DOM the theme re-renders later. The overlay's own slides
		   never match — it lives outside any .product-carousel. */
		document.addEventListener('click', (e) => {
			const img = e.target.closest('.product-carousel .swiper-slide img')
			if (!img) return
			e.preventDefault()

			/* Collect from the clicked image's own carousel so the slide
			   order matches what the customer is looking at, and the
			   mobile/desktop galleries never cross-contaminate. */
			this.openedBy = img
			this.collectImages(img.closest('.product-carousel'))
			const high = this.zoomUrl(img)
			this.open(this.images.findIndex((entry) => entry.high === high))
		})
	}

	/* The gallery's currentSrc is sized for the gallery, not for a 3x
	   zoom — bump the CDN width param to 4096, the top rung
	   snippets/product-media.liquid already requests. The CDN never
	   upscales, so this returns the stored master for anything narrower
	   and a 4096 cut only for the few camera shots that are wider; it is
	   fetched only when the lightbox opens. */
	zoomUrl(img) {
		const url = img.currentSrc || img.src
		return url.replace(/([?&])width=\d+/, '$1width=4096')
	}

	collectImages(carousel) {
		this.images = []

		carousel.querySelectorAll('.swiper-slide img').forEach((img) => {
			const high = this.zoomUrl(img)
			if (!this.images.some((entry) => entry.high === high)) {
				/* low = the exact file the gallery is showing, i.e. already
				   in the browser cache — it paints instantly in the slide
				   while the sharp file streams in behind (see open()). */
				this.images.push({ low: img.currentSrc || img.src, high })
			}
		})
	}

	attachKeyboardHandlers() {
		document.addEventListener('keydown', (e) => {
			if (!this.overlay.classList.contains('active')) return

			if (e.key === 'Escape') this.close()
			if (e.key === 'ArrowLeft') this.navigate(-1)
			if (e.key === 'ArrowRight') this.navigate(1)
		})
	}

	/* Arrows and arrow keys always change image, zoomed or not — the
	   zoomed slide lock only exists to stop PAN gestures escaping into a
	   swipe. zoom.out() is a no-op at 1x, and otherwise unlocks the
	   slider synchronously via zoomChange before slideNext/Prev runs. */
	navigate(direction) {
		if (!this.swiper) return
		this.swiper.zoom.out()
		if (direction > 0) this.swiper.slideNext()
		else this.swiper.slidePrev()
	}

	open(index = 0) {
		/* Every slide starts on the gallery-sized file the page already
		   has in cache, so open and swipe both paint instantly; the sharp
		   4096px files stream in behind and swap in place (see the
		   sharpen pass below). */
		const wrapper = this.overlay.querySelector('.swiper-wrapper')
		wrapper.innerHTML = this.images
			.map(
				({ low, high }) => `
        <div class="swiper-slide">
          <div class="swiper-zoom-container">
            <img src="${low}" data-zoom-src="${high}" alt="">
          </div>
        </div>`
			)
			.join('')

		const multiple = this.images.length > 1
		this.overlay
			.querySelectorAll('.zoom-nav')
			.forEach((btn) => (btn.style.display = multiple ? 'flex' : 'none'))

		/* Show the overlay BEFORE initialising Swiper — inside a
		   display:none parent it would measure zero width and every slide
		   would land on top of the first. */
		this.overlay.classList.add('active')
		/* Engage the shared body scroll lock (global.js watches this
		   class), instead of a bare overflow style iOS ignores. */
		document.body.classList.add('overflow-hidden')

		this.swiper = new Swiper(this.overlay.querySelector('.zoom-swiper'), {
			loop: multiple,
			initialSlide: index,
			zoom: {
				maxRatio: 3,
			},
			/* Two-finger horizontal trackpad swipes change slides, matching
			   how the rest of the site scrolls; forceToAxis keeps vertical
			   scrolling for the wheel-zoom handler above. */
			mousewheel: {
				forceToAxis: true,
			},
			on: {
				/* While zoomed, panning past the image edge must NOT escape
				   into a slide change (Swiper's default edge behavior — it
				   reads as a glitchy random swipe on mobile). Lock slide
				   switching whenever scale > 1; panning is unaffected because
				   the zoom module translates the image inside the slide, not
				   the wrapper. zoomChange reads the scale from its argument —
				   the property hasn't settled when the event fires. */
				zoomChange: (swiper, scale) => {
					const zoomed = scale > 1
					swiper.allowSlideNext = !zoomed
					swiper.allowSlidePrev = !zoomed
				},
				/* swiper.update() re-applies the params' allow flags
				   (verified on 11.0.5), so a mid-zoom resize or orientation
				   change would silently drop the lock — re-assert it. */
				update: (swiper) => {
					const zoomed = swiper.zoom.scale > 1
					swiper.allowSlideNext = !zoomed
					swiper.allowSlidePrev = !zoomed
				},
			},
		})

		/* Focus the overlay itself, not the close button — programmatic
		   focus can trigger :focus-visible (mobile WebKit especially) and
		   draw a ring around the X. Keyboard users still Tab straight to
		   it. Same pattern as the in-store tip modal (global.js). */
		trapFocus(this.overlay)

		/* Sharpen pass: fetch each slide's 4096px file off-DOM and swap it
		   in when it lands — same element, so an in-progress zoom keeps
		   its transform. Active image first so nothing competes with it,
		   the rest once it arrives (they stay perfectly viewable on their
		   cached gallery files in the meantime). */
		const sharpen = (img, onDone) => {
			const high = img.dataset.zoomSrc
			if (!high || img.src === high) {
				if (onDone) onDone()
				return
			}
			const loader = new Image()
			loader.onload = () => {
				img.src = high
				if (onDone) onDone()
			}
			loader.onerror = () => {
				if (onDone) onDone()
			}
			loader.src = high
		}
		const slideImgs = [...this.overlay.querySelectorAll('.zoom-swiper img')]
		const activeImg =
			this.overlay.querySelector('.swiper-slide-active img') || slideImgs[0]
		sharpen(activeImg, () => {
			slideImgs.forEach((img) => {
				if (img !== activeImg) sharpen(img)
			})
		})
	}

	close() {
		this.overlay.classList.remove('active')
		document.body.classList.remove('overflow-hidden')

		if (this.zoomFrame) {
			cancelAnimationFrame(this.zoomFrame)
			this.zoomFrame = null
		}
		this.pendingScale = null

		if (this.swiper) {
			this.swiper.destroy(true, true)
			this.swiper = null
		}
		this.overlay.querySelector('.swiper-wrapper').innerHTML = ''

		/* Gallery images aren't natively focusable — grant tabindex so
		   focus genuinely returns to the image that opened the overlay. */
		if (this.openedBy && !this.openedBy.hasAttribute('tabindex')) {
			this.openedBy.setAttribute('tabindex', '-1')
		}
		/* Tear down the trap WITHOUT its focus call, then restore focus
		   with preventScroll. removeTrapFocus()'s bare .focus() scrolls
		   the opener into view, which (with html scroll-behavior:smooth)
		   visibly slid the PDP on close whenever the image sat partly
		   outside the viewport. */
		removeTrapFocus()
		if (this.openedBy) this.openedBy.focus({ preventScroll: true })
	}
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		new ProductImageZoom()
	})
} else {
	new ProductImageZoom()
}
